import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { handlePath } from "./pathHandlers";
import { useSupabaseAuth } from "../AuthContext/AuthContext"; 

type DeepLinkData = {
  url: string;
};

const useDeepLink = (): void => {
  const navigate = useNavigate();
  const initialUrlProcessed = useRef(false);
  const { useAuthStore } = useSupabaseAuth(); // Get auth context
  const user = useAuthStore((state: any) => state.user); // Get current user

  useEffect(() => {
    let listenerHandle: PluginListenerHandle | null = null;

    // Function to handle the deep link URL
    const handleDeepLink = (url: string): void => {
      try {
        console.log("Handling deep link URL:", url);
        
        // Check if user is authenticated before handling deep link
        if (!user) {
          navigate("/welcome");
          return;
        }
        
        const slug = url.split("://").pop(); // Extract slug from the URL
        
        if (slug) {
          handlePath(slug, navigate);
        } else {
          console.warn("No valid slug found in the URL:", url);
          navigate("/"); // Fallback to home page if no slug found
        }
      } catch (error) {
        console.error("Error handling deep link:", error);
        navigate("/"); // Fallback to home page on error
      }
    };

    // Function to set up listeners
    const setupListeners = async (): Promise<void> => {
      try {
        // Set up the URL open listener
        listenerHandle = await CapacitorApp.addListener(
          "appUrlOpen",
          (data: DeepLinkData) => {
            console.log("App opened with URL:", data.url);
            handleDeepLink(data.url); // Process the deep link URL
          }
        );

        // Check for initial URL if the app was launched with one, but only once
        if (!initialUrlProcessed.current) {
          const initialUrl = await CapacitorApp.getLaunchUrl();
          if (initialUrl?.url) {
            console.log("App launched with initial URL:", initialUrl.url);
            handleDeepLink(initialUrl.url); // Process the initial deep link URL
            initialUrlProcessed.current = true; // Mark as processed to prevent redirect loops
          }
          initialUrlProcessed.current = true; // Mark as checked even if no URL to avoid future checks
        }
      } catch (error) {
        console.error("Error setting up deep link listeners:", error);
      }
    };

    // Set up the listeners
    setupListeners();

    // Cleanup function to remove the listener when the component is unmounted
    return (): void => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [navigate, user]); // Added user to dependencies
};

export default useDeepLink;